import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import LeftArrow_3 from "../../../../assets/img/icons/LeftArrow_3.svg";
import RightArrow_3 from "../../../../assets/img/icons/RightArrow_3.svg";
import AddSingleMemberDropdown from "../../../../components/CustomDropdown/AddSingleMemberDropdown";
import FilterDropDownStyle from "../../../../components/DropdownStyle/FilterDropDownStyle";
import Dropdown from "../../../../components/ReactSelectDropdown";
import { filterOption } from "../../../../components/ReactSelectDropdown/ReactSelectDropdown";
import ClearFilterButton from "../../../../components/Search/clearFilterButton";
import DatePicker from "../../../../components/SingleDatePicker/SingleDatePicker";
import { FilterButton } from "../../../../styledComponents/buttons";
import {
  CommonFlex,
  FilterLabel,
  IconArrow,
  InputWithLabelSection,
} from "../../../../styledComponents/common";
import { RouteMapFilterContainer } from "./filterStyles";

const ALL_OFFICES = t => ({ label: `(${t("all_teams")})`, value: null });

const RouteMapFilter = ({
  selectedOrganization,

  getOrganizationMembersShortList,
  getOfficesShortList,

  orgMembersShortList,
  officesShortList,
  selectOffice,
  officeDetails,

  selectedMember,
  setSelectedMember,
  selectedOffice,
  setSelectedOffice,
  selectedDate,
  setSelectedDate,

  getRouteMapLocations,
  getSingleEmployeeClockInOutList,
  getTaskCheckInOutList,

  profile,
  clockInOutList,
}) => {
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const { t } = useTranslation();
  const [memberOptions, setMemberOptions] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);

  // Only apply “initial default” once when Redux is empty
  const initDefaultsApplied = useRef(false);

  // Normalize date (Redux might store string or moment)
  const toMoment = d => (moment.isMoment(d) ? d : d ? moment(d) : moment());
  const safeDate = toMoment(selectedDate);
  const isToday = safeDate.isSame(moment(), "day");

  const role = () => localStorage.getItem("selected_organization_role");

  const getLocalUser = () => ({
    id: localStorage.getItem("user_id"),
    name:
      localStorage.getItem("user_fullName") ||
      localStorage.getItem("email") ||
      "Me",
    email: localStorage.getItem("email"),
    avatar: profile?.profileData?.thumbnail,
    phone: profile?.profileData?.phone,
  });

  const makePayload = (date, memberId) => ({
    organization_id: selectedOrganization.id,
    start: toMoment(date).startOf("day").unix(),
    end: toMoment(date).endOf("day").unix(),
    ...(memberId ? { employee: memberId } : {}),
  });

  // Helper function to extract clock time range from list
  const getClockTimeRange = clockList => {
    if (!clockList || clockList.length === 0) return null;

    const firstClockIn = clockList.find(c => c.clockin_timestamp);
    const lastClockOut = [...clockList]
      .reverse()
      .find(c => c.clockout_timestamp);

    if (!firstClockIn) return null;

    // If no clock out, use today's end of day as the end time
    const endTime = lastClockOut
      ? lastClockOut.clockout_timestamp
      : moment().endOf("day").unix();

    return {
      start: firstClockIn.clockin_timestamp,
      end: endTime,
    };
  };

  // Step 1: Only call getSingleEmployeeClockInOutList first
  const loadFor = useCallback(
    (date, memberId) => {
      if (!selectedOrganization?.id) return;
      const payload = makePayload(date, memberId);
      // Only trigger clock in/out list first - DO NOT call getRouteMapLocations here
      getSingleEmployeeClockInOutList(payload);
    },
    [selectedOrganization?.id, getSingleEmployeeClockInOutList],
  );

  const handleSelectMember = useCallback(
    member => {
      if (!(selectedOrganization?.id && member?.id)) return;
      loadFor(selectedDate || moment(), member.id);
      setSelectedMember(member);
    },
    [selectedOrganization?.id, loadFor, selectedDate, setSelectedMember],
  );

  const clearSelectedMember = () => setSelectedMember(null);

  // Fetch lists for admin/owner when org changes
  useEffect(() => {
    if (!selectedOrganization?.id) return;
    const r = role();
    if (r === "admin" || r === "owner") {
      getOfficesShortList({ organization_id: selectedOrganization.id });
      getOrganizationMembersShortList({
        organization_id: selectedOrganization.id,
      });
    }
  }, [selectedOrganization]);

  // Build office options; default to All Offices only if Redux didn’t set one at all
  useEffect(() => {
    if (!officesShortList) return;
    const list = [
      ALL_OFFICES(t),
      ...officesShortList.map(o => ({ label: o.name, value: o.id, ...o })),
    ];
    setOfficeOptions(list);
    if (selectedOffice == null) {
      setSelectedOffice(list[0]);
    }
  }, [officesShortList, t, selectedOffice, setSelectedOffice]);

  useEffect(() => {
    if (
      selectedOffice?.value != null &&
      officeDetails &&
      orgMembersShortList?.length
    ) {
      const officeMembers = orgMembersShortList.filter(m =>
        officeDetails.employees?.some(e => e.user.id === m.id),
      );
      setMemberOptions(officeMembers);
    } else {
      setMemberOptions([]);
    }
  }, [selectedOffice, officeDetails, orgMembersShortList]);

  useEffect(() => {
    if (initDefaultsApplied.current) return;
    if (!selectedOrganization?.id) return;

    if (!selectedDate) {
      setSelectedDate(moment());
    }

    if (selectedMember?.id) {
      loadFor(selectedDate || moment(), selectedMember.id);
      initDefaultsApplied.current = true;
      return;
    }

    const me = getLocalUser();
    if (me?.id) {
      handleSelectMember(me);
      initDefaultsApplied.current = true;
      return;
    }

    const r = role();
    if (r === "admin" || r === "owner") {
      if (selectedOffice?.value != null && memberOptions.length > 0) {
        handleSelectMember(memberOptions[0]);
        initDefaultsApplied.current = true;
        return;
      }
      if (
        (selectedOffice?.value === null || selectedOffice == null) &&
        orgMembersShortList?.length > 0
      ) {
        handleSelectMember(orgMembersShortList[0]);
        initDefaultsApplied.current = true;
        return;
      }
    }
  }, [
    selectedOrganization?.id,
    selectedDate,
    selectedMember?.id,
    selectedOffice?.value,
    memberOptions,
    orgMembersShortList,
    handleSelectMember,
    loadFor,
    setSelectedDate,
  ]);
  // Step 2: After clockInOutList is populated, call getRouteMapLocations with extracted times
  useEffect(() => {
    if (!clockInOutList || clockInOutList.length === 0) return;
    if (!selectedOrganization?.id || !selectedMember?.id) return;

    const clockTimeRange = getClockTimeRange(clockInOutList);

    // Only call APIs if we have a valid time range
    if (!clockTimeRange) return;

    const routeLocationsPayload = {
      organization_id: selectedOrganization.id,
      employee: selectedMember.id,
      start: clockTimeRange.start,
      end: clockTimeRange.end,
    };

    getRouteMapLocations(routeLocationsPayload);
    getTaskCheckInOutList(routeLocationsPayload);
  }, [
    clockInOutList,
    selectedOrganization?.id,
    selectedMember?.id,
    getRouteMapLocations,
    getTaskCheckInOutList,
  ]);
  const isAllOffices = selectedOffice?.value === null;
  const membersListForDropdown = useMemo(() => {
    if (isAllOffices) return orgMembersShortList || [];
    if (memberOptions && memberOptions.length) return memberOptions;
    return orgMembersShortList || [];
  }, [isAllOffices, orgMembersShortList, memberOptions]);

  const singleDateChange = direction => {
    const next =
      direction === "right"
        ? moment(safeDate).add(1, "days")
        : moment(safeDate).subtract(1, "days");
    selectDate(next);
  };

  const selectToday = () => selectDate(moment());

  const selectDate = date => {
    if (!selectedOrganization?.id) return;
    loadFor(date, selectedMember?.id);
    setSelectedDate(date);
  };

  return (
    <RouteMapFilterContainer
      columns={isOperationType ? "240px auto 57px 90px" : "auto 57px 90px"}
    >
      {(localStorage.getItem("selected_organization_role") === "admin" ||
        localStorage.getItem("selected_organization_role") === "owner") && (
        <>
          {/* team */}
          {isOperationType && (
            <InputWithLabelSection style={{ minWidth: `240px` }}>
              <FilterLabel>{t("team")}</FilterLabel>
              <Dropdown
                isSearchable
                isClearable={selectedOffice?.value}
                value={selectedOffice}
                placeholder="Select team..."
                onChange={e => {
                  e ? selectOffice(e) : selectOffice(ALL_OFFICES(t));
                }}
                options={officeOptions}
                styles={FilterDropDownStyle({ height: "40px" })}
                filterOption={filterOption}
                components={{
                  IndicatorSeparator: () => null,
                  ClearIndicator: props => <ClearFilterButton {...props} />,
                }}
              />
            </InputWithLabelSection>
          )}

          {/* Date */}
          <InputWithLabelSection>
            <FilterLabel>{t("date")}</FilterLabel>
            <CommonFlex gap="4px">
              <DatePicker
                id="routeMapFilterDate"
                date={safeDate}
                onDateChange={selectDate}
                placeholder={"Select date..."}
                allowPreviousDates
                disableFutureDates
                width="200px"
              />
              <CommonFlex gap="4px" margin="1.5px 0 0 0">
                <IconArrow
                  onClick={() => singleDateChange("left")}
                  padding="5px"
                  size="40px"
                  src={LeftArrow_3}
                />
                <IconArrow
                  margin="0 0 0 2px"
                  onClick={() => {
                    if (!isToday) singleDateChange("right");
                  }}
                  padding="5px"
                  size="40px"
                  src={RightArrow_3}
                  disabled={isToday}
                />
              </CommonFlex>
              <FilterButton
                type="gray"
                padding="10px"
                onClick={selectToday}
                margin="0 0 0 2px"
              >
                Today
              </FilterButton>
            </CommonFlex>
          </InputWithLabelSection>

          {/* Assignee */}
          <InputWithLabelSection>
            <FilterLabel>{t("assignee")}</FilterLabel>
            <div style={{ width: `40px` }}>
              <AddSingleMemberDropdown
                assigneeManage={selectedMember}
                updateState={handleSelectMember}
                membersList={membersListForDropdown}
                clearAssignee={clearSelectedMember}
              />
            </div>
          </InputWithLabelSection>
        </>
      )}

      {(localStorage.getItem("selected_organization_role") === "member" ||
        localStorage.getItem("selected_organization_role") === "employee") && (
        <>
          {/* Date */}
          <InputWithLabelSection>
            <FilterLabel>{t("date")}</FilterLabel>
            <CommonFlex gap="4px">
              <DatePicker
                id="routeMapFilterDate"
                date={safeDate}
                onDateChange={selectDate}
                placeholder={"Select date..."}
                allowPreviousDates
                disableFutureDates
              />
              <CommonFlex gap="4px">
                <IconArrow
                  onClick={() => singleDateChange("left")}
                  padding="5px"
                  size="40px"
                  src={LeftArrow_3}
                />
                <IconArrow
                  margin="0 0 0 2px"
                  onClick={() => {
                    if (!isToday) singleDateChange("right");
                  }}
                  padding="5px"
                  size="40px"
                  src={RightArrow_3}
                  disabled={isToday}
                />
              </CommonFlex>
              {/* <div style={{ margin: "0 0 0 14px" }}> */}
              <FilterButton
                type="gray"
                padding="10px"
                onClick={selectToday}
                margin="0 0 0 2px"
              >
                Today
              </FilterButton>
              {/* </div> */}
            </CommonFlex>
          </InputWithLabelSection>

          {/* Assignee (self only) */}
          <InputWithLabelSection>
            <FilterLabel>{t("assignee")}</FilterLabel>
            <div style={{ width: `40px` }}>
              <AddSingleMemberDropdown
                assigneeManage={selectedMember}
                updateState={handleSelectMember}
                membersList={selectedMember ? [selectedMember] : []}
                clearAssignee={clearSelectedMember}
              />
            </div>
          </InputWithLabelSection>
        </>
      )}
    </RouteMapFilterContainer>
  );
};

export default RouteMapFilter;
